import { useState, useEffect } from 'react';
import { supabase as publicSupabase } from '@/integrations/supabase/client';

// Reuse shared Supabase client to avoid multiple GoTrue instances

interface PublicEventData {
  id: string;
  slug: string;
  public_title: string;
  public_description: string;
  public_location: string;
  public_start_at: string;
  public_end_at: string;
  hero_image_url: string | null;
  rsvp_enabled: boolean;
}

interface PublicEventRSVP {
  id: string;
  public_page_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  status: string;
  guest_count: number;
  message: string | null;
  created_at: string;
  updated_at: string;
}

export function usePublicEvent() {
  const [loading, setLoading] = useState(false);

  const getPublicEvent = async (slug: string): Promise<PublicEventData | null> => {
    try {
      setLoading(true);
      
      const { data, error } = await publicSupabase
        .from('public_event_pages')
        .select(`
          id,
          slug,
          public_title,
          public_description,
          public_location,
          public_start_at,
          public_end_at,
          hero_image_url,
          rsvp_enabled
        `)
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching public event:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getPublicEvent:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const submitPublicRSVP = async (
    publicPageId: string, 
    rsvpData: Omit<PublicEventRSVP, 'id' | 'public_page_id' | 'created_at' | 'updated_at'>
  ): Promise<boolean> => {
    try {
      setLoading(true);
      
      const { error } = await publicSupabase
        .from('public_event_rsvp')
        .insert({
          public_page_id: publicPageId,
          ...rsvpData
        });

      if (error) {
        console.error('Error submitting RSVP:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in submitPublicRSVP:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    getPublicEvent,
    submitPublicRSVP
  };
}